import { useEffect, useRef } from "react";
import CustomHeader from "../../components/customHeader";
import snowflakeNeedleHaystack from "../../assets/images/pngs/snowflake-page/snowflake-needle-haystack.png";
import snowflakeAutoSavings from "../../assets/images/svgs/snowflake-page/snowflake-auto-savings.svg";
import snowflakeWorkloads from "../../assets/images/pngs/snowflake-page/snowflake-workloads.png";
import snowflakeCostSavings from "../../assets/images/pngs/snowflake-page/snowflake-cost-savings-team-r.png";
import snowflakeHiddenTableCost from "../../assets/images/pngs/snowflake-page/snowflake-hidden-table-cost-r.png";
import snowflakeExpertQueryAdvice from "../../assets/images/pngs/snowflake-page/snowflake-query-insights-agent-2.png";
import snowflakePreventMistakes from "../../assets/images/pngs/snowflake-page/snowflake-prevent-mistakes.png";
import { ProductDemoFooter } from "../../components/snowflake/productDemo";

const Snowflake = () => {
  const secLinking = useRef();
  useEffect(() => {
    secLinking.current = new MutationObserver(() => {
      const hash = window.location.hash.substring(1);
      console.log(hash);
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView();
        secLinking.current.disconnect();
      }
    });
    secLinking.current.observe(document, { childList: true, subtree: true });
    return () => {
      secLinking.current.disconnect();
    };
  }, []);

  return (
    <div style={{ background: "white" }}>
      <CustomHeader
        title={
          <div>
            Reduce{" "}
            <span className="font-[600] text-[#247EFE]">Snowflake Costs</span>{" "}
            by 30%
          </div>
        }
        heading="Snowflake"
      />
      <div id="warehouse-auto-savings" className="lg:p-12 lg:pb-0 py-[60px] snowflake">
        <div className="warehouse-insights">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              Snowflake compute, managed autonomously.
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Just switch on your <b>Altimate AI Teammate</b>, and immediately realize 10% - 20% savings on your 
                Snowflake compute bills with no human work required. Altimate AI goes far beyond other 
                solutions, automatically adjusting warehouse sizing, autoscaling, and suspension settings 
                thousands of times per day to perfectly match workload requirements.
              </div>
              <img src={snowflakeAutoSavings} alt="Snowflake Auto Saving" className="max-w-[900px] mx-auto" />
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />

      <div id="cost-team-sport" className="lg:p-12 lg:pb-0 py-[60px] mt-16 snowflake">
        <div className="warehouse-insights">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              Make platform cost savings a team sport.
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Your <b>Altimate AI Teammate</b> continuously identifies opportunities for savings and efficiency in your Snowflake 
                environment, and provides tailored views to each of your internal teams.  With just a click, an opportunity can be 
                assigned to the person on the snowflake consumer team with the exact set of change instructions generated by the AI 
                teammate, and followed up until its done.
              </div>
              <img src={snowflakeCostSavings} alt="Snowflake Auto Saving" className="max-w-[900px] mx-auto" />
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />

      <div id="workloads" className="lg:p-12 lg:pb-0 py-[60px] mt-16 snowflake">
        <div className="warehouse-insights">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              Workload-level understanding.
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Plenty of solutions track cost, efficiency, and performance at the virtual warehouse level, or at the 
                level of an individual query. 
                Your <b>Altimate AI Teammate</b> looks deeper, with an understanding of entire query workloads, and 
                correlates workload run metrics with a mutlitude of other events -- warehouse configuration 
                changes, code changes, task failures -- pinpointing the exact causes of shifts in performance and cost 
                of your workloads over time.
                </div>
              <img src={snowflakeWorkloads} alt="Snowflake Auto Saving" className="max-w-[900px] mx-auto" />
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />

      <div id="hidden-table-cost" className="lg:p-12 lg:pb-0 py-[60px] mt-16 snowflake">
        <div className="warehouse-insights">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              The hidden cost of your tables, exposed.
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Snowflake charges for storage, and storage is relatively cheap.  But the true cost of a table goes 
                far beyond its size on disk, in ways that are often totally opaque.  Your <b>Altimate AI Teammate</b> digs below 
                the surface to uncover the real cost of a table, which includes not just the storage cost but the compute cost 
                of the pipelines that build the table and keep it fresh, and the costs of Snowflake's hidden "failsafe" storage that 
                can balloon to many times the size of the table itself under certain usage patterns.  And then the AI goes a step 
                further to determine if the table is needed in the first place … is it ever read?  Can it be dropped and the pipeline jobs stopped entirely?
                <br />
                <br />
                This deep analysis of not only the total costs but the value provided by every dataset can help your teams dramatically 
                reduce cost and simplify pipelines in ways they've never been able to before.
              </div>
              <img src={snowflakeHiddenTableCost} alt="Snowflake Auto Saving" className="max-w-[900px] mx-auto" />
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />

      <div id="expert-query-advice" className="lg:p-12 lg:pb-0 py-[60px] mt-16 snowflake">
        <div className="warehouse-insights">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              Expert query advice, with just a click.
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Your <b>Altimate AI Teammate</b> analyzes every query along dozens of dimensions, and applies its deep knowledge 
                of cost / performance improvement techniques to identify areas where the query can be improved, delivering precise
                change instructions to your platform team and Snowflake consumers. 
                With this level of SQL expertise on board, you can quickly target and refactor the most frequent and most 
                expensive queries running in your environment, ensuring you get the most value out of Snowflake for every dollar spent.
              </div>
              <img src={snowflakeExpertQueryAdvice} alt="Snowflake Auto Saving" className="max-w-[900px] mx-auto" />
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />

      <div id="needles-haystacks" className="lg:p-12 lg:pb-0 py-[60px] mt-16 snowflake">
        <div className="warehouse-insights w-full text-center">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              Needles from Haystacks, in seconds.
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Your <b>Altimate AI Teammate</b> dives into individual queries, quickly parsing and understanding thousands of lines 
                of SQL code, calling out the few lines that matter and making recommendations on exactly what should be changed to 
                tune and optimize the query.
              </div>
            </div>
            <div className="max-w-[1200px] mx-auto">
              <img src={snowflakeNeedleHaystack} alt="Snowflake Auto Saving" className="w-[900px] mx-auto" />
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />

      <div id="catch-mistakes" className="lg:p-12 lg:pb-0 py-[60px] mt-16 snowflake">
        <div className="warehouse-insights">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              Prevent costly mistakes during development.
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Altimate’s AI Teammates work side-by-side with your SQL developers, understanding the SQL queries they are building, 
                detecting anti-patterns and guiding on best practices as queries are being written. Immediate feedback is provided 
                in the IDEs your team is already using, as well as within your existing Git workflows and CI/CD pipelines.
              </div>
              <img src={snowflakePreventMistakes} alt="Snowflake Auto Saving" className="max-w-[900px] mx-auto" />
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />

      <div id="catch-mistakes" className="lg:p-12 lg:pb-0 py-[60px] mt-16 snowflake">
        <div className="warehouse-insights">
          <div className="insights-text">
            <h1 className="lexend text-[#082247] md:text-[36px] text-[36px] font-semibold mb-[10px] text-center max-w-[1032px]">
              <>
              Meet your new Altimate AI Teammate today!
              </>
            </h1>
            <div className="max-w-[900px] mx-auto text-center">
              <div className="lexend text-[#365178] text-[16px] font-[400] mb-6">
                Your Altimate AI Teammate is ready get to work, reducing your platform costs and improving your developers' efficiency. 
              </div>
            </div>
          </div>
        </div>
      </div>
      <ProductDemoFooter />
      
    </div>
  );
};

export default Snowflake;
